import React, { useState } from 'react';

import { Flex } from 'components';

import cx from 'classnames';
import styles from './Details.scss';

import { EntityNotesAndHours } from 'domains/workOrder/serviceRequest/components/EntityNotesAndHours';
import { ActiveChannelTypeEnum } from 'constants-channel';
import { TaskTimeForm } from 'domains/workOrder/serviceRequest/components/TaskTime';
import {
  convertMinutesToHoursAndMinutes,
  calculateTotalMinutes,
} from 'date-time-manipulations';
import { useServiceRequestTaskTimeEnable } from 'lane-shared/hooks/useServiceRequestTaskTimeEnable';
import { ServiceRequest } from 'graphql-query-contracts';
import { ServiceRequestDataDisplay } from './components/ServiceRequestDataDisplay';
import { ServiceRequestAttachments } from './components/ServiceRequestAttachments';
import { useServiceRequestPermissions } from './hooks/useServiceRequestPermissions';
import { UserType } from 'lane-shared/types/User';

export function DetailsTab({
  channel,
  user,
  serviceRequestData,
  serviceRequestFeatures,
  onServiceRequestUpdated,
}: {
  channel?: { _id: string; type: ActiveChannelTypeEnum; slug: string };
  user: UserType;
  serviceRequestData: ServiceRequest;
  serviceRequestFeatures: any;
  onServiceRequestUpdated: any;
}) {
  const isTaskTimeEnabled = useServiceRequestTaskTimeEnable();

  const { hours: initialHours, minutes: initialMinutes } =
    convertMinutesToHoursAndMinutes(serviceRequestData.taskTimeInMinutes ?? 0);
  const [editHours, setEditHours] = useState(false);

  const handleTaskTimeUpdate = ({
    hours,
    minutes,
  }: {
    hours: number;
    minutes: number;
  }) => {
    const taskTimeInMinutes = calculateTotalMinutes(hours, minutes);

    onServiceRequestUpdated({ taskTimeInMinutes });
  };

  const { hasAttachFilesPermission, isStaffUser } =
    useServiceRequestPermissions(user, channel);
  const isPropertyChannel = channel?.type === ActiveChannelTypeEnum.Property;

  return (
    <div className={styles.DetailsTab}>
      {channel && channel.slug && (
        <ServiceRequestDataDisplay
          serviceRequestData={serviceRequestData}
          isPropertyChannel={isPropertyChannel}
          channel={channel}
        />
      )}

      <Flex
        direction="row"
        width="full"
        style={{ flexWrap: 'wrap' }}
        className={styles.attachmentsTaskTimeFlexRow}
        justify="space-between"
        gap={5}
      >
        {/* Attachments */}
        <Flex direction="column">
          {serviceRequestFeatures.updateServiceRequest &&
            serviceRequestData.attachmentRefId && (
              <ServiceRequestAttachments
                serviceRequest={serviceRequestData}
                attachmentRefId={serviceRequestData.attachmentRefId}
                hasAttachFilesPermission={hasAttachFilesPermission}
                isStaffUser={isStaffUser}
                className={`${styles.attachmentsCard} ${styles.ServiceRequestAttachments}`}
              />
            )}
        </Flex>

        {/* Task Time */}
        {isTaskTimeEnabled && (
          <Flex direction="column">
            <div
              className={cx(styles.taskTimeFormContainer, {
                [styles.editMode]: editHours,
              })}
            >
              <TaskTimeForm
                initialHours={initialHours}
                initialMinutes={initialMinutes}
                onEntityUpdated={handleTaskTimeUpdate}
                editHours={editHours}
                setEditHours={setEditHours}
              />
            </div>
          </Flex>
        )}
      </Flex>

      {/* Notes and Hours */}
      <Flex direction="column" className={styles.notesAndHoursFlexRow}>
        <EntityNotesAndHours
          entityData={{
            hours: null, // We are hiding hours (hideHours)
            minutes: null, // We are hiding minutes (hideHours)
            notes: serviceRequestData.notes ?? '',
          }}
          onEntityUpdated={onServiceRequestUpdated}
          hideHours
        />
      </Flex>
    </div>
  );
}
